
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent
} from '@mui/material';
import { marked } from 'marked';

const REPORT_TYPES = [
  { key: 'aggregate_analysis', title: '🎯 Combined Analysis', color: '#ffd700', icon: '🎯', priority: true },
  { key: 'market_report', title: '📊 Market Analysis', color: '#00ff00', icon: '📊' },
  { key: 'sentiment_report', title: '💬 Social Sentiment', color: '#00bfff', icon: '💬' },
  { key: 'news_report', title: '📰 News Analysis', color: '#ff6b35', icon: '📰' },
  { key: 'fundamentals_report', title: '💼 Fundamentals Analysis', color: '#9c27b0', icon: '💼' },
  { key: 'research_agent_output', title: '🔬 Research Agent', color: '#e91e63', icon: '🔬' },
  { key: 'stock_agent_output', title: '📈 Stock Agent', color: '#00e676', icon: '📈' },
  { key: 'ta_agent_output', title: '📉 Technical Analysis', color: '#ff9800', icon: '📉' },
  { key: 'investment_plan', title: '🎯 Investment Plan', color: '#ffc107', icon: '🎯' },
  { key: 'trader_investment_plan', title: '📈 Trading Team Plan', color: '#00bcd4', icon: '📈' },
  { key: 'risky_history', title: '🔥 Risky Analyst', color: '#ff4444', icon: '🔥' },
  { key: 'safe_history', title: '🛡️ Safe Analyst', color: '#4444ff', icon: '🛡️' },
  { key: 'neutral_history', title: '⚖️ Neutral Analyst', color: '#888888', icon: '⚖️' },
  { key: 'current_risky_response', title: '🔥 Risky Analysis', color: '#ff4444', icon: '🔥' },
  { key: 'current_safe_response', title: '🛡️ Safe Analysis', color: '#4444ff', icon: '🛡️' },
  { key: 'current_neutral_response', title: '⚖️ Neutral Analysis', color: '#888888', icon: '⚖️' },
  { key: 'judge_decision', title: '👨‍💼 Portfolio Manager Decision', color: '#00ff00', icon: '👨‍💼' },
  { key: 'final_trade_decision', title: '⚡ Final Trade Decision', color: '#00ff00', icon: '⚡' },
  { key: 'guru_aswath_damodaran', title: '📐 Aswath Damodaran Analysis', color: '#4169e1', icon: '📐' },
  { key: 'guru_ben_graham', title: '📚 Ben Graham Analysis', color: '#8b7355', icon: '📚' },
  { key: 'guru_bill_ackman', title: '🦈 Bill Ackman Analysis', color: '#dc143c', icon: '🦈' },
  { key: 'guru_cathie_wood', title: '🚀 Cathie Wood Analysis', color: '#7b68ee', icon: '🚀' },
  { key: 'guru_charlie_munger', title: '🎓 Charlie Munger Analysis', color: '#c0c0c0', icon: '🎓' },
  { key: 'guru_michael_burry', title: '⚠️ Michael Burry Analysis', color: '#ff6347', icon: '⚠️' },
  { key: 'guru_mohnish_pabrai', title: '🧘 Mohnish Pabrai Analysis', color: '#ff8c00', icon: '🧘' },
  { key: 'guru_peter_lynch', title: '🔍 Peter Lynch Analysis', color: '#ff1493', icon: '🔍' },
  { key: 'guru_phil_fisher', title: '🔬 Phil Fisher Analysis', color: '#2e8b57', icon: '🔬' },
  { key: 'guru_rakesh_jhunjhunwala', title: '🐂 Rakesh Jhunjhunwala Analysis', color: '#ff4500', icon: '🐂' },
  { key: 'guru_stanley_druckenmiller', title: '🌍 Stanley Druckenmiller Analysis', color: '#20b2aa', icon: '🌍' },
  { key: 'guru_warren_buffett', title: '💰 Warren Buffett Analysis', color: '#ffd700', icon: '💰' },
  { key: 'guru_technical_analyst', title: '📉 Technical Analyst Report', color: '#ff9800', icon: '📉' },
  { key: 'guru_fundamentals_analyst', title: '💼 Fundamentals Analyst Report', color: '#9c27b0', icon: '💼' },
  { key: 'guru_growth_analyst', title: '📈 Growth Analyst Report', color: '#00e676', icon: '📈' },
  { key: 'guru_news_sentiment_analyst', title: '📰 News Sentiment Report', color: '#ff6b35', icon: '📰' },
  { key: 'guru_sentiment_analyst', title: '💬 Sentiment Analyst Report', color: '#00bfff', icon: '💬' },
  { key: 'guru_valuation_analyst', title: '⚖️ Valuation Analyst Report', color: '#e91e63', icon: '⚖️' },
  { key: 'guru_portfolio_decision', title: '🏆 Guru Portfolio Decision', color: '#ffd700', icon: '🏆' }
];

function ReportCarousel({ 
  analysisData, 
  title = "Analysis Reports",
  showScrollHint = true 
}) {
  if (!analysisData) return null;

  // Configure marked for better rendering
  marked.setOptions({
    breaks: true,
    gfm: true,
    headerIds: false,
    mangle: false
  });

  const renderMarkdown = (content) => {
    try {
      return marked.parse(content);
    } catch (error) {
      console.error('Error parsing markdown:', error);
      return content.replace(/\n/g, '<br/>');
    }
  };

  // Filter available reports based on data structure
  const reports = analysisData.reports || analysisData;
  
  // Extract risk debate state data if available
  const riskDebateState = reports.risk_debate_state || {};
  
  // Create a combined data object that includes both direct reports and risk debate state
  const allReportsData = {
    ...reports,
    ...riskDebateState
  };
  
  const availableReports = REPORT_TYPES.filter(report => {
    const data = allReportsData[report.key];
    return data && typeof data === 'string' && data.trim() !== '';
  });

  if (availableReports.length === 0) return null;

  return (
    <Box>
      <Typography variant="h6" color="primary" gutterBottom>
        {title}
      </Typography>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 1.5, md: 2 }, 
          overflowX: { xs: 'hidden', md: 'auto' }, 
          pb: 2,
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#2a2a2a',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#00ff00',
            borderRadius: '4px',
          },
        }}
      >
        {availableReports.map((report) => (
          <Card 
            key={report.key}
            sx={{ 
              minWidth: { xs: '100%', md: '400px' }, 
              maxWidth: { xs: '100%', md: '400px' }, 
              backgroundColor: 'background.paper',
              border: (report.key === 'final_trade_decision' || report.key === 'aggregate_analysis')
                ? `2px solid ${report.color}` 
                : `1px solid ${report.color}`,
              boxShadow: report.key === 'aggregate_analysis' ? `0 0 10px ${report.color}` : undefined
            }}
          >
            <CardContent>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ color: report.color }}
              >
                {report.title}
              </Typography>
              <Box 
                sx={{ 
                  maxHeight: '400px', 
                  overflowY: 'auto',
                  '& pre': { 
                    whiteSpace: 'pre-wrap', 
                    fontSize: '0.875rem',
                    backgroundColor: '#2a2a2a',
                    padding: '8px',
                    borderRadius: '4px',
                    overflow: 'auto'
                  },
                  '& code': {
                    backgroundColor: '#2a2a2a',
                    padding: '2px 4px',
                    borderRadius: '2px',
                    fontSize: '0.875rem'
                  },
                  '& h1': { 
                    color: '#00bfff', 
                    fontSize: '1.25rem', 
                    fontWeight: 'bold',
                    mt: 2, 
                    mb: 1,
                    borderBottom: '1px solid #333'
                  },
                  '& h2': { 
                    color: '#00bfff', 
                    fontSize: '1.1rem', 
                    fontWeight: 'bold',
                    mt: 2, 
                    mb: 1 
                  },
                  '& h3': { 
                    color: '#00bfff', 
                    fontSize: '1rem', 
                    fontWeight: 'bold',
                    mt: 1.5, 
                    mb: 1 
                  },
                  '& h4, & h5, & h6': { 
                    color: '#00bfff', 
                    fontSize: '0.9rem', 
                    fontWeight: 'bold',
                    mt: 1, 
                    mb: 0.5 
                  },
                  '& p': {
                    mb: 1,
                    lineHeight: 1.6
                  },
                  '& ul, & ol': {
                    pl: 2,
                    mb: 1
                  },
                  '& li': {
                    mb: 0.5
                  },
                  '& blockquote': {
                    borderLeft: '3px solid #00bfff',
                    pl: 2,
                    ml: 0,
                    fontStyle: 'italic',
                    color: '#aaa'
                  },
                  '& table': { 
                    width: '100%', 
                    fontSize: '0.75rem',
                    borderCollapse: 'collapse',
                    mb: 1
                  },
                  '& th': { 
                    padding: '8px', 
                    border: '1px solid #555',
                    backgroundColor: '#333',
                    fontWeight: 'bold'
                  },
                  '& td': { 
                    padding: '6px', 
                    border: '1px solid #555'
                  },
                  '& a': {
                    color: '#00bfff',
                    textDecoration: 'none'
                  },
                  '& a:hover': {
                    textDecoration: 'underline'
                  },
                  '& strong': {
                    fontWeight: 'bold',
                    color: '#fff'
                  },
                  '& em': {
                    fontStyle: 'italic'
                  }
                }}
                dangerouslySetInnerHTML={{ 
                  __html: renderMarkdown(allReportsData[report.key]) 
                }}
              />
            </CardContent>
          </Card>
        ))}
      </Box>

      {showScrollHint && (
        <Box mt={3} textAlign="center" sx={{ display: { xs: 'none', md: 'block' } }}>
          <Typography variant="body2" color="textSecondary">
            Scroll horizontally to view all available reports →
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ReportCarousel;
